import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SimulationTrackerState } from 'src/app/state/simulation-tracker/simulation-tracker.state';
import { SimulationTracker } from '../interfaces/simulation-tracker';
import { map, Observable } from 'rxjs';
import { FavoritedSimulationTrackers } from '../interfaces/favorited-simulation-trackers';
import { AgentSettings } from '../interfaces/agent-settings';
import { CellColoring } from '../interfaces/simulation-results-cell-coloring';
import { SimulationTrackerUserUsageReport } from '../interfaces/simulation-tracker-user-usage-report';
import { SimulationTrackerStatusCount } from '../interfaces/simulation-tracker-status-count';
import { SimulationTrackerStatusMessage } from '../enums/simulation-tracker-status';

@Injectable({
  providedIn: 'root',
})
export class SimulationTrackerService {
  constructor(private httpClient: HttpClient) {}

  public getSimulationTracker(simulationTrackerId: string) {
    return this.httpClient.get<SimulationTracker>(
      `/api/simulationTracker/${simulationTrackerId}`
    );
  }

  
    public isSimulationNameAvailable(simulationName: string) {
    return this.httpClient.get<boolean>(
      `/api/simulationTracker/checkSimulationNameInUse/${simulationName}`
    );
  }
  
  public getSimulationTrackerAgentSettings(simulationTrackerId: string) {
    return this.httpClient.get<AgentSettings>(
      `/api/simulationTracker/${simulationTrackerId}/agentSettings`
    );
  }

  public getSimulationTrackerCellColorings(simulationTrackerId: string) {
    return this.httpClient
      .get<{ $id: number; $values: CellColoring[] }>(
        `/api/simulationTracker/${simulationTrackerId}/cellColorings`
      )
      .pipe(map((r: { $id: number; $values: CellColoring[] }) => r.$values));
  }

  public getSimulationTrackers(): Observable<SimulationTracker[]> {
    return this.httpClient
      .get<{ $id: number; $values: SimulationTracker[] }>(
        `/api/simulationTracker/all`
      )
      .pipe(
        map((r: { $id: number; $values: SimulationTracker[] }) => r.$values)
      );
  }

  public getScenarioSimulationTrackers(
    scenarioId: string
  ): Observable<SimulationTracker[]> {
    return this.httpClient
      .get<{ $id: number; $values: SimulationTracker[] }>(
        `/api/simulationTracker/scenarios/${scenarioId}`
      )
      .pipe(
        map((r: { $id: number; $values: SimulationTracker[] }) => r.$values)
      );
  }

  public createSimulationTracker(studyAreaId: string, terrainGridId: string) {
    return this.httpClient.post<SimulationTracker>(`/api/simulationTracker`, {
      studyAreaId,
      terrainGridId,
    });
  }

  public updateSimulationTrackerStatus(
    simulationTrackerId: string,
    status: SimulationTrackerState
  ): Observable<SimulationTracker> {
    return this.httpClient.patch<SimulationTracker>(
      `/api/simulationTracker/${simulationTrackerId}`,
      { status }
    );
  }


    public deleteMultipleSimulationTracker(simulationTrackerIds: string[]) {
     return this.httpClient.request('delete', `/api/simulationTracker`, {
    body: simulationTrackerIds,
  });
  }

  public getFavoritedSimulationTrackers() {
    return this.httpClient.get<FavoritedSimulationTrackers>(
      `/api/simulationTracker/Favorite`
    );
  }

  public favoriteSimulationTracker(simulationTrackerId: string) {
    return this.httpClient.patch<SimulationTracker>(
      `/api/simulationTracker/Favorite/${simulationTrackerId}`,
      null
    );
  }

  public removeFavoritedSimulationTracker(simulationTrackerId: string) {
    return this.httpClient.patch<SimulationTracker>(
      `/api/simulationTracker/RemoveFavorite/${simulationTrackerId}`,
      null
    );
  }

  public getSimulationTrackerUsage(): Observable<
    SimulationTrackerUserUsageReport[]
  > {
    return this.httpClient
      .get<{ $id: number; $values: SimulationTrackerUserUsageReport[] }>(
        `/api/simulationTracker/usagereport`
      )
      .pipe(
        map(
          (r: { $id: number; $values: SimulationTrackerUserUsageReport[] }) =>
            r.$values
        )
      );
  }

  public getSimulationTrackerStatusReport(): Observable<SimulationTrackerStatusCount[]> {
    return this.httpClient
      .get<Record<string, number>>('/api/simulationTracker/statusreport')
      .pipe(
        map((response) =>
          Object.entries(response)
            .filter(([key]) => key !== '$id') // ignore system property
            .map(([status, count]) => ({
              status: status as SimulationTrackerStatusMessage,
              count
            }))
        )
      );
  }

}
